import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { compose } from 'utils/compose';

import { Box, Typography, DialogContent, DialogTitle, Grid, RadioGroup, FormControlLabel } from '@mui/material';

import { DialogFooter, DialogActionButton } from 'components/Dialog';
import RadioButton from 'components/RadioButton';

import { EDIT_NOTICE } from 'constants/verbiage';
import withMediaQuery from 'constants/mediaQuery';

import { useStyles } from './styles';

// HOC to inject styles into class component
// no-dd-sa:javascript-code-style/parameter-name
const withUseStyles = (WrappedComponent) => {
  return function WithStylesWrapper(props) {
    const classes = useStyles();
    return <WrappedComponent {...props} classes={classes} />;
  };
};

class EditWindowDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      earliest_time: '08:00AM',
      latest_time: '12:00PM',
      windowOptions: [
        { label: '08:00AM - 12:00PM', value: '08:00AM,12:00PM' },
        { label: '12:00PM - 04:00PM', value: '12:00PM,04:00PM' },
        { label: '04:00PM - 08:00PM', value: '04:00PM,08:00PM' },
      ],
    };
  }

  componentDidMount() {
    const { windowOptions } = this.state;
    const { deliveryStore } = this.props.store;
    const { delivery } = deliveryStore;
    const { shipment } = delivery;

    const window = windowOptions[0].value.split(',');
    // Get edited data
    const timezone = shipment?.timezone ? shipment.timezone : 'America/Los_Angeles';
    const editedFromTs = deliveryStore.getEditedDeliveryField('dropoff_earliest_ts');
    const editedFromTime = moment(editedFromTs).tz(timezone).format('hh:mmA');
    const editedToTs = deliveryStore.getEditedDeliveryField('dropoff_latest_ts');
    const editedToTime = moment(editedToTs).tz(timezone).format('hh:mmA');

    this.setState({
      earliest_time: editedFromTs ? editedFromTime : window[0],
      latest_time: editedToTs ? editedToTime : window[1],
    });
  }

  handleTimeWindowChange = (value) => {
    const window = value.split(',');
    this.setState({
      earliest_time: window[0],
      latest_time: window[1],
    });
  };

  save = () => {
    const { deliveryStore } = this.props.store;
    const { delivery } = deliveryStore;
    const { earliest_time, latest_time } = this.state;
    const { shipment } = delivery;
    const timezone = shipment?.timezone ? shipment.timezone : 'America/Los_Angeles';
    const dropoffDate = moment(shipment.dropoff_earliest_ts).tz(timezone).format('DD/MM/YYYY');
    // no-dd-sa:javascript-code-style/assignment-name
    const earliest_ts = moment.tz(dropoffDate + ' ' + earliest_time, 'DD/MM/YYYY hh:mmA', timezone).toISOString();
    // no-dd-sa:javascript-code-style/assignment-name
    const latest_ts = moment.tz(dropoffDate + ' ' + latest_time, 'DD/MM/YYYY hh:mmA', timezone).toISOString();

    deliveryStore.setEditedDeliveryField('dropoff_earliest_ts', earliest_ts);
    deliveryStore.setEditedDeliveryField('dropoff_latest_ts', latest_ts);

    deliveryStore.saveAndCloseDialog();
  };

  render() {
    const { windowOptions } = this.state;
    const { classes, store, mediaQuery } = this.props;
    const { deliveryStore } = store;
    const { delivery, tokenRemainingTimeString } = deliveryStore;

    if (!delivery) return null;

    const { shipment } = delivery;
    const window = windowOptions[0].value.split(',');
    const timezone = shipment?.timezone ? shipment.timezone : 'America/Los_Angeles';
    const fromTime = moment(shipment.dropoff_earliest_ts).tz(timezone).format('hh:mm A');
    const toTime = moment(shipment.dropoff_latest_ts).tz(timezone).format('hh:mm A');

    // Get edited data
    const editedFromTs = deliveryStore.getEditedDeliveryField('dropoff_earliest_ts');
    const editedFromTime = moment(editedFromTs).tz(timezone).format('hh:mmA');
    const editedToTs = deliveryStore.getEditedDeliveryField('dropoff_latest_ts');
    const editedToTime = moment(editedToTs).tz(timezone).format('hh:mmA');
    const savedWindowValue = editedFromTs && editedToTs ? editedFromTime + ',' + editedToTime : window[0] + ',' + window[1];

    return (
      <Box sx={classes.container}>
        <DialogTitle sx={classes.titleBox}>
          <Grid
            container
            justifyContent="space-between"
            alignItems={mediaQuery.isMobile ? 'flex-start' : 'center'}
            direction={mediaQuery.isMobile ? 'column' : 'row'}
            spacing={mediaQuery.isMobile ? 1 : 0}
          >
            <Grid sx={classes.dialogTitle}>
              <span style={{ fontWeight: 600 }}>Edit Delivery Window</span>
            </Grid>
            <Grid sx={classes.dialogTimer}>
              <Box component="span" sx={classes.underlined}>
                Session time:
              </Box>
              <Box component="span" sx={classes.timerCounter}>
                {tokenRemainingTimeString}
              </Box>
            </Grid>
          </Grid>
          <Box pt={2} pb={mediaQuery.isMobile ? 2 : 3} sx={classes.noticeBox}>
            <span style={{ fontWeight: 600 }}>{EDIT_NOTICE}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography sx={{ ...classes.darkGrayText, ...classes.infoBox }}>
              Please make final changes 24 hours before noon of the Scheduled Delivery Date
            </Typography>
            <Box py={3}>
              <Typography sx={classes.marginBottom}>
                <span style={{ fontWeight: 600 }}>Scheduled Delivery Window</span>
              </Typography>
              <Typography sx={classes.darkGrayText}>
                <span>
                  {fromTime} - {toTime}
                </span>
              </Typography>
            </Box>
            <Box py={1}>
              <Typography sx={classes.marginBottom}>
                <span style={{ fontWeight: 600 }}>Preferred Delivery Window</span>
              </Typography>
              <RadioGroup name="preferred_window" defaultValue={savedWindowValue || windowOptions[0].value}>
                {windowOptions.map((wd) => (
                  <FormControlLabel
                    control={<RadioButton />}
                    label={
                      <Box component="span" sx={{ ...classes.darkGrayText, verticalAlign: 'sub' }}>
                        {wd.label}
                      </Box>
                    }
                    value={wd.value}
                    onChange={(e) => this.handleTimeWindowChange(e.target.value)}
                  />
                ))}
              </RadioGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogFooter>
          <DialogActionButton onClick={() => deliveryStore.closeEditDialog()} variant="outlined" color="primary" disableElevation>
            Cancel
          </DialogActionButton>
          <DialogActionButton onClick={this.save} variant="contained" color="primary" disableElevation>
            Save
          </DialogActionButton>
        </DialogFooter>
      </Box>
    );
  }
}

const EditWindowDialogCompose = compose(inject('store'), observer)(EditWindowDialog);

export default withUseStyles(withMediaQuery()(EditWindowDialogCompose));
